(() => {
  const LIME_GREEN = { name: "Lime Green", sku: "SV570610", screenSampleHex: "#B1C74E" };
  const FALLBACK_COLORS = [
    LIME_GREEN,
    { name: "Kelly Green", sku: "SV573835", screenSampleHex: "#188B54" },
    { name: "Navy Blue", sku: "SV570608", screenSampleHex: "#303C4E" },
    { name: "Royal Blue", sku: "SV570605", screenSampleHex: "#2E419B" },
    { name: "Red", sku: "SV570603", screenSampleHex: "#D1353A" },
    { name: "Yellow", sku: "SV570599", screenSampleHex: "#EACC40" },
    { name: "Black", sku: "SV570593", screenSampleHex: "#272526" },
  ];

  class GamedaySixInchBagProduct extends HTMLElement {
    connectedCallback() {
      if (this.dataset.sixBagReady === "true") return;
      this.dataset.sixBagReady = "true";

      this.choiceGrid = this.querySelector("[data-six-bag-choices]");
      this.searchInput = this.querySelector("[data-six-bag-search]");
      this.slotButtons = Array.from(this.querySelectorAll("[data-six-bag-slot]"));
      this.propertyInputs = Object.fromEntries(
        Array.from(this.querySelectorAll("[data-six-bag-property]")).map((input) => [input.dataset.sixBagProperty, input]),
      );
      this.colors = window.EXOGamedayBagColorMatchData?.colors?.length
        ? window.EXOGamedayBagColorMatchData.colors
        : FALLBACK_COLORS;
      const lime = this.colors.find((color) => color.name === LIME_GREEN.name) || this.colors[0] || LIME_GREEN;
      this.state = { activeSlot: "a", a: lime, b: lime };

      this.slotButtons.forEach((button) => button.addEventListener("click", () => {
        this.state.activeSlot = button.dataset.sixBagSlot;
        this.renderChoices(this.searchInput?.value || "");
        this.update();
      }));
      this.querySelector("[data-six-apply-both]")?.addEventListener("click", () => {
        const color = this.state[this.state.activeSlot];
        this.state.a = color;
        this.state.b = color;
        this.renderChoices(this.searchInput?.value || "");
        this.update();
      });
      this.searchInput?.addEventListener("input", () => this.renderChoices(this.searchInput.value));
      if (!this.syncExternalProductImage()) {
        this.externalProductImageObserver = new MutationObserver(() => {
          if (this.syncExternalProductImage()) {
            this.externalProductImageObserver?.disconnect();
            this.externalProductImageObserver = null;
          }
        });
        this.externalProductImageObserver.observe(document.documentElement, { childList: true, subtree: true });
      }
      this.renderChoices();
      this.update();
    }

    disconnectedCallback() {
      this.externalProductImageObserver?.disconnect();
      this.externalProductImageObserver = null;
    }

    syncExternalProductImage() {
      const approvedImage = this.dataset.approvedProductImage;
      if (!approvedImage) return false;
      const reviewImages = Array.from(document.querySelectorAll(".jdgm-write-review-modal__product-image img"));
      reviewImages.forEach((image) => {
        image.src = approvedImage;
        image.alt = "Gameday Flags regulation six-inch cornhole bags";
      });
      return reviewImages.length > 0;
    }

    renderChoices(query = "") {
      if (!this.choiceGrid) return;
      const normalized = query.trim().toLowerCase();
      const colors = this.colors.filter((color) =>
        !normalized || `${color.name} ${color.sku}`.toLowerCase().includes(normalized),
      );
      const selected = this.state[this.state.activeSlot];

      this.choiceGrid.replaceChildren();
      colors.forEach((color) => {
        const button = document.createElement("button");
        const preview = document.createElement("span");
        const copy = document.createElement("span");
        const name = document.createElement("strong");
        const sku = document.createElement("small");
        button.type = "button";
        button.setAttribute("aria-pressed", String(color.sku === selected?.sku));
        button.setAttribute("aria-label", `${color.name}, Ottertex ${color.sku}, apply to Team ${this.state.activeSlot.toUpperCase()}`);
        preview.className = "gameday-six-bags__choice-proof";
        preview.style.setProperty("--choice-swatch", color.screenSampleHex || "#777777");
        name.textContent = color.name;
        sku.textContent = `FWD ${color.sku}`;
        copy.append(name, sku);
        button.append(preview, copy);
        button.addEventListener("click", () => {
          this.state[this.state.activeSlot] = color;
          this.renderChoices(this.searchInput?.value || "");
          this.update();
        });
        this.choiceGrid.append(button);
      });
    }

    update() {
      const colorA = this.state.a || LIME_GREEN;
      const colorB = this.state.b || LIME_GREEN;
      const selected = this.state[this.state.activeSlot] || colorA;
      const stitchA = this.stitchColorFor(colorA);
      const stitchB = this.stitchColorFor(colorB);
      const selectedStitch = this.state.activeSlot === "a" ? stitchA : stitchB;
      const configuration = `TEAM A ${colorA.name} / ${colorA.sku} + TEAM B ${colorB.name} / ${colorB.sku}`;

      const proofColor = selected.sku === LIME_GREEN.sku ? "transparent" : (selected.screenSampleHex || LIME_GREEN.screenSampleHex);
      this.style.setProperty("--six-active-proof-color", proofColor);
      this.querySelector("[data-six-bag-chip]")?.style.setProperty("--swatch", selected.screenSampleHex || LIME_GREEN.screenSampleHex);
      this.querySelector("[data-six-bag-chip]")?.style.setProperty("--stitch-swatch", selectedStitch === "Silver" ? "#C5C9C9" : "#090A09");
      this.querySelector('[data-six-proof-swatch="a"]')?.style.setProperty("--snapshot-swatch", colorA.screenSampleHex || LIME_GREEN.screenSampleHex);
      this.querySelector('[data-six-proof-swatch="b"]')?.style.setProperty("--snapshot-swatch", colorB.screenSampleHex || LIME_GREEN.screenSampleHex);
      this.querySelector('[data-six-proof-swatch="a"]')?.style.setProperty("--snapshot-stitch", stitchA === "Silver" ? "#C5C9C9" : "#090A09");
      this.querySelector('[data-six-proof-swatch="b"]')?.style.setProperty("--snapshot-stitch", stitchB === "Silver" ? "#C5C9C9" : "#090A09");
      this.querySelectorAll("[data-six-proof-team-snapshot]").forEach((snapshot) => {
        snapshot.classList.toggle("is-active", snapshot.dataset.sixProofTeamSnapshot === this.state.activeSlot);
      });
      this.slotButtons.forEach((button) => {
        const active = button.dataset.sixBagSlot === this.state.activeSlot;
        button.setAttribute("aria-pressed", String(active));
      });

      this.writeText('[data-six-slot-label="a"]', colorA.name);
      this.writeText('[data-six-slot-label="b"]', colorB.name);
      this.writeText("[data-six-proof-team]", `TEAM ${this.state.activeSlot.toUpperCase()} / LIVE COLOR PROOF`);
      this.writeText("[data-six-proof-name]", selected.name);
      this.writeText("[data-six-proof-sku]", `FWD ${selected.sku}`);
      this.writeText("[data-six-proof-stitch]", `${selectedStitch.toUpperCase()} STITCHING`);
      this.writeText('[data-six-proof-snapshot-name="a"]', colorA.name);
      this.writeText('[data-six-proof-snapshot-sku="a"]', `FWD ${colorA.sku}`);
      this.writeText('[data-six-proof-snapshot-stitch="a"]', `${stitchA.toUpperCase()} STITCHING`);
      this.writeText('[data-six-proof-snapshot-name="b"]', colorB.name);
      this.writeText('[data-six-proof-snapshot-sku="b"]', `FWD ${colorB.sku}`);
      this.writeText('[data-six-proof-snapshot-stitch="b"]', `${stitchB.toUpperCase()} STITCHING`);
      this.writeText("[data-six-bag-name]", selected.name);
      this.writeText("[data-six-bag-sku]", `FWD ${selected.sku}`);
      this.writeText("[data-six-bag-configuration]", configuration.toUpperCase());
      this.setProperty("configuration", configuration);
      this.setProperty("colorA", colorA.name);
      this.setProperty("skuA", colorA.sku);
      this.setProperty("hexA", colorA.screenSampleHex || LIME_GREEN.screenSampleHex);
      this.setProperty("stitchA", stitchA);
      this.setProperty("colorB", colorB.name);
      this.setProperty("skuB", colorB.sku);
      this.setProperty("hexB", colorB.screenSampleHex || LIME_GREEN.screenSampleHex);
      this.setProperty("stitchB", stitchB);
      this.updateQuote(configuration, colorA, colorB);
    }

    stitchColorFor(color) {
      return color?.sku === "SV570593" || color?.name === "Black" ? "Silver" : "Black";
    }

    updateQuote(configuration, colorA, colorB) {
      const quote = this.querySelector("[data-six-bag-quote-link]");
      if (!quote || !quote.href) return;
      const url = new URL(quote.href, window.location.origin);
      url.searchParams.set("configuration", configuration);
      url.searchParams.set("team_a", `${colorA.name} / ${colorA.sku}`);
      url.searchParams.set("team_b", `${colorB.name} / ${colorB.sku}`);
      quote.href = url.toString();
    }

    writeText(selector, value) {
      const node = this.querySelector(selector);
      if (node) node.textContent = value;
    }

    setProperty(key, value) {
      if (this.propertyInputs[key]) this.propertyInputs[key].value = value;
    }
  }

  if (!customElements.get("gameday-six-inch-bag-product")) {
    customElements.define("gameday-six-inch-bag-product", GamedaySixInchBagProduct);
  }
})();
